import React, { useMemo, useState } from "react";

import type { ColDef, RowSelectionOptions } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { api } from "../../../shared/hooks/api";

ModuleRegistry.registerModules([AllCommunityModule]);

type Row = {
    materialId: string;
    materialCode: string;
    materialName: string;
    storage: string;
    missingCount: number;
};

const rowSelection: RowSelectionOptions = {
    mode: "multiRow",
    headerCheckbox: false,
};

const MissingMaterialsTab = ({ onSelectionChange }: { onSelectionChange?: (rows: any[]) => void }) => {
    const [{ data, isLoading }, fetchMaterials] = api.get(
        `/materials/missing-materials/`,
    );

    const rowData = useMemo<Row[]>(() => {
        const list = Array.isArray(data) ? data : [];
        return list.map((item: any) => {
            return {
                materialId: item?.material?.id ?? "",
                materialCode: item?.material?.material_code ?? "N/A",
                materialName: item?.material?.name ?? "N/A",
                storage: item?.material?.storage ?? "N/A",
                missingCount: typeof item?.missing_quantity === "number" ? item.missing_quantity : 0,
            };
        });


    }, [data]);

    const myTheme = themeQuartz.withParams({
        headerBackgroundColor: "#f2f4fa",
        headerFontWeight: "bold",
        headerHeight: 32,
    });

    const [columnDefs, setColumnDefs] = useState<ColDef<Row>[]>([
        {
            headerName: "Mã vật tư",
            field: "materialCode",
            width: 150,
            sortable: true,
            filter: true,
            cellRenderer: (params: any) => {
                const code = params.value as string;
                const id = params?.data?.materialId as string | undefined;
                const href = id ? `/materials/${encodeURIComponent(id)}` : `/materials/${encodeURIComponent(code)}`;
                return (
                    <a href={href} style={{ textDecoration: "underline", color: "#1976d2" }}>
                        {code}
                    </a>
                );
            },
        },
        { headerName: "Tên vật tư", field: "materialName", flex: 1, minWidth: 220, sortable: true, filter: true },
        { headerName: "Kho", field: "storage", width: 180, sortable: true, filter: true },
        {
            headerName: "Số lượng thiếu",
            field: "missingCount",
            width: 180,
            sortable: true,
            filter: "agNumberColumnFilter",
            cellClass: "ag-right-aligned-cell",
        },
    ]);

    const handleSelectionChange = (rows: Row[]) => {
        // Map selected grid rows to minimal payload with material_id and missing quantity
        const selectedMaterials = rows.map((row) => {
            const full = (data as any[]).find((m: any) => {
                const code = m.material.material_code ?? (m.material.id ? String(m.id).slice(0, 8) : "N/A");
                return code === row.materialCode;
            }) || {};

            return {
                ...full.material,
                missing_quantity: row.missingCount,
            };
        });


        onSelectionChange?.(selectedMaterials);
    };

    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true,
        };
    }, []);

    return (
        <div>
            <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowSelection={rowSelection}
                domLayout="autoHeight"
                unSortIcon={true}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 25, 50]}
                theme={myTheme}
                rowHeight={32}
                onSelectionChanged={(event: any) => {
                    const rows = (event.api.getSelectedRows() || []) as Row[];
                    handleSelectionChange(rows);
                }}
            />
        </div>
    );
};

export default MissingMaterialsTab;